// The end of the claim window, for the owner (F-IAM-13, spec/13 §8.2): moved balances whose window ends within 30 days,
// and those whose window has ended — personal details deleted, play time lapsed, the money still owed — with their
// totals, and the owner's sign-off that writes the ended ones off. Shown only to `credit:write_off` (the owner's by
// default); the server checks it again. The write-off asks for confirmation in a centred dialog.

import type { CarriedOverWriteOffList } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, Table, type TableColumn, TextField } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { useScopeIds } from '../../shell/scope';
import { Section } from '../../ui';
import { useCarriedOverWriteOffs, useWriteOffCarriedOver } from './queries';

type Item = CarriedOverWriteOffList['data'][number];

export function WriteOffPanel(): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const list = useCarriedOverWriteOffs(scope.organizationId, true);
  const writeOff = useWriteOffCarriedOver(scope.organizationId);
  const [confirming, setConfirming] = useState(false);
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState('');
  const day = (date: string) => format.date(`${date}T12:00:00.000Z`, 'medium');

  const columns = useMemo<readonly TableColumn<Item>[]>(
    () => [
      {
        key: 'player',
        header: t('players.moved.writeOff.column.player'),
        cell: (item) => (item.username === null ? <span className="console-subtle">{t('players.moved.writeOff.removed')}</span> : <span>@{item.username}</span>),
      },
      {
        key: 'state',
        header: t('players.moved.column.status'),
        cell: (item) => <Badge tone={item.state === 'ended' ? 'warn' : 'info'}>{t(`players.moved.writeOff.${item.state}`)}</Badge>,
      },
      { key: 'ends', header: t('players.moved.writeOff.column.ends'), cell: (item) => day(item.claimWindowEndsOn) },
      {
        key: 'money',
        header: t('players.moved.writeOff.column.money'),
        numeric: true,
        cell: (item) => <span className="console-money">{format.money(item.cash)}</span>,
      },
      {
        key: 'time',
        header: t('players.moved.writeOff.column.time'),
        numeric: true,
        cell: (item) => (item.timeSeconds === 0 ? <span className="console-subtle">{t('players.moved.claim.none')}</span> : <span className="console-money">{format.duration(item.timeSeconds)}</span>),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `day` closes over `format`, already a dependency.
    [t, format],
  );

  if (list.data === undefined || list.data.data.length === 0) return null;
  const ended = list.data.data.filter((item) => item.state === 'ended');
  const total = format.money(list.data.endedTotal);

  return (
    <Section
      className="console-writeoff"
      title={t('players.moved.writeOff.title')}
      description={t('players.moved.writeOff.intro')}
      actions={
        ended.length > 0 ? (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            {t('players.moved.writeOff.approve', { amount: total })}
          </Button>
        ) : undefined
      }
    >
      <p className="console-writeoff__totals">
        <span>{t('players.moved.writeOff.endedTotal', { amount: total })}</span>
        <span>{t('players.moved.writeOff.expiringTotal', { amount: format.money(list.data.expiringTotal) })}</span>
      </p>
      <Table caption={t('players.moved.writeOff.title')} captionHidden columns={columns} rows={list.data.data} rowKey={(item) => item.id} />
      {writeOff.data !== undefined ? (
        <p role="status">{t('players.moved.writeOff.done', { amount: format.money(writeOff.data.amount) })}</p>
      ) : null}
      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        size="md"
        title={t('players.moved.writeOff.dialogTitle')}
        description={t('players.moved.writeOff.dialogBody', { amount: total, count: ended.length })}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t('players.moved.writeOff.cancel')}
            </Button>
            <Button
              variant="danger"
              busy={writeOff.isPending}
              disabled={!checked}
              onClick={() =>
                writeOff.mutate(
                  { importedPlayerIds: ended.map((item) => item.id), ...(reason.trim().length >= 3 ? { reason: reason.trim() } : {}) },
                  { onSuccess: () => setConfirming(false) },
                )
              }
            >
              {t('players.moved.writeOff.submit')}
            </Button>
          </>
        }
      >
        <Checkbox label={t('players.moved.writeOff.confirm')} checked={checked} onCheckedChange={(value) => setChecked(value === true)} />
        <TextField label={t('players.moved.writeOff.reason')} value={reason} onChange={(event) => setReason(event.target.value)} />
        {writeOff.isError ? (
          <p className="console-claim__error" role="alert">
            {problem.text(writeOff.error, { fallback: t('players.moved.writeOff.error') })}
          </p>
        ) : null}
      </Dialog>
    </Section>
  );
}
